import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../api';

/**
 * Owns contact messages, the new-message notification count (polled every
 * 30s regardless of which admin tab is open — it feeds the sidebar badge),
 * and the details/reply modals.
 */
export default function useContactMessages({ activeTab, showNotification }) {
  const [contactMessages, setContactMessages] = useState([]);
  const [loadingContactMessages, setLoadingContactMessages] = useState(false);
  const [contactStats, setContactStats] = useState(null);
  const [selectedContactMessage, setSelectedContactMessage] = useState(null);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [contactFilters, setContactFilters] = useState({ status: '', search: '' });
  const [newContactCount, setNewContactCount] = useState(0);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyForm, setReplyForm] = useState({ response: '', responseMethod: 'Email' });

  const fetchContactMessages = async () => {
    setLoadingContactMessages(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      if (contactFilters.status) params.append('status', contactFilters.status);
      if (contactFilters.search) params.append('search', contactFilters.search);

      const response = await fetch(`${API_BASE_URL}/api/admin/contact?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setContactMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching contact messages:', error);
    } finally {
      setLoadingContactMessages(false);
    }
  };

  const fetchContactStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/contact/stats`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) setContactStats(await response.json());
    } catch (error) {
      console.error('Error fetching contact stats:', error);
    }
  };

  // Keeping the latest count in a ref would be cleaner, but this mirrors the
  // original comparison (read current state to detect newly-arrived messages).
  const fetchNewContactCount = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/contact/notifications/new-count`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setNewContactCount((oldCount) => {
          if (data.count > oldCount && oldCount > 0) {
            showNotification(`You have ${data.count} new contact message(s)`, 'success');
          }
          return data.count || 0;
        });
      }
    } catch (error) {
      console.error('Error fetching new contact count:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'contact') {
      fetchContactMessages();
      fetchContactStats();
    }
  }, [activeTab, contactFilters]);

  // Poll for new contact messages every 30 seconds, regardless of active tab.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchNewContactCount();
      if (activeTab === 'contact') fetchContactMessages();
    }, 30000);
    fetchNewContactCount();
    return () => clearInterval(interval);
  }, [activeTab]);

  const handleViewContactMessage = async (messageId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/contact/${messageId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        setSelectedContactMessage(await response.json());
        setShowContactDetails(true);
        await fetchContactMessages();
        await fetchNewContactCount();
      }
    } catch (error) {
      console.error('Error fetching contact message:', error);
      showNotification('Failed to load message', 'error');
    }
  };

  const closeContactDetails = () => {
    setShowContactDetails(false);
    setSelectedContactMessage(null);
  };

  const handleUpdateContactStatus = async (messageId, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/contact/${messageId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (response.ok) {
        await fetchContactMessages();
        if (selectedContactMessage && selectedContactMessage.id === messageId) {
          setSelectedContactMessage(await response.json());
        }
        showNotification('Status updated successfully', 'success');
      } else {
        const err = await response.json();
        showNotification(err.error || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error updating contact status:', error);
      showNotification('Network error while updating status', 'error');
    }
  };

  const closeReplyModal = () => {
    setShowReplyModal(false);
    setReplyForm({ response: '', responseMethod: 'Email' });
  };

  const handleReplyToMessage = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/contact/${selectedContactMessage.id}/reply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(replyForm),
      });

      if (response.ok) {
        const updated = await response.json();
        setSelectedContactMessage(updated);
        closeReplyModal();
        await fetchContactMessages();
        showNotification('Reply sent successfully', 'success');

        if (replyForm.responseMethod === 'WhatsApp' && selectedContactMessage.phone) {
          const phone = selectedContactMessage.phone.replace(/[^0-9]/g, '');
          const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(replyForm.response)}`;
          window.open(whatsappUrl, '_blank');
        }
      } else {
        const err = await response.json();
        showNotification(err.error || 'Failed to send reply', 'error');
      }
    } catch (error) {
      console.error('Error replying to message:', error);
      showNotification('Network error while sending reply', 'error');
    }
  };

  return {
    contactMessages, loadingContactMessages, contactStats, contactFilters, setContactFilters,
    selectedContactMessage, showContactDetails, closeContactDetails,
    newContactCount, setNewContactCount, fetchContactMessages,
    showReplyModal, setShowReplyModal, closeReplyModal, replyForm, setReplyForm,
    handleViewContactMessage, handleUpdateContactStatus, handleReplyToMessage,
  };
}
